import type {DiscriminatedTileRequest, ImageBitmapTileKind, PixelTileKind, TileResponse} from "@cm/three-texture-streaming"
import {AbstractTileLoader} from "@cm/three-texture-streaming"
import {CorsAwareWorker, type IWorkerLike} from "@cm/browser-utils"

export type TileKinds = {
    PixelTile: PixelTileKind
    ImageBitmapTile: ImageBitmapTileKind
}

export class TextureTileLoader extends AbstractTileLoader<TileKinds> {
    constructor(workerUrl: string) {
        super()
        this.worker = new CorsAwareWorker(`${new URL(import.meta.url).origin}/${workerUrl}`, {type: "module", name: "TextureTileLoader"})
        this.worker.addEventListener("message", (event: MessageEvent<FromWorkerMessage>) => this.handleWorkerMessage(event))
    }

    override dispose(): void {
        this.worker.terminate()
    }

    protected override startRequest(requestId: number, request: DiscriminatedTileRequest<TileKinds>, abortSignal: AbortSignal): void {
        abortSignal.addEventListener("abort", () => this.worker.postMessage({type: "cancelTile", requestId} satisfies CancelTile), {once: true})
        this.worker.postMessage({type: "loadTile", requestId, ...request} satisfies LoadTile)
    }

    private handleWorkerMessage(event: MessageEvent<FromWorkerMessage>): void {
        this.pendingMessages.push(event.data)
        if (!this.drainScheduled) {
            this.drainScheduled = true
            requestAnimationFrame(() => this.drainPendingMessages())
        }
    }

    private drainPendingMessages(): void {
        this.drainScheduled = false
        for (const msg of this.pendingMessages.splice(0)) {
            if (msg.type === "tileLoaded") {
                this.resolveRequest(msg.requestId, msg.response)
            } else if (msg.type === "tileError") {
                this.rejectRequest(msg.requestId, msg.error)
            }
        }
    }

    private readonly worker: IWorkerLike
    private pendingMessages: FromWorkerMessage[] = []
    private drainScheduled = false
}

// ---- Message types ----

export type RequestId = number

export type LoadTile = {
    type: "loadTile"
    requestId: RequestId
} & DiscriminatedTileRequest<TileKinds>

export type CancelTile = {
    type: "cancelTile"
    requestId: RequestId
}

export type ToWorkerMessage = LoadTile | CancelTile

export type TileLoaded = {
    type: "tileLoaded"
    requestId: RequestId
    response: TileResponse<TileKinds>
}

export type TileError = {
    type: "tileError"
    requestId: RequestId
    error: string
}

export type FromWorkerMessage = TileLoaded | TileError
